import { useMemo, useState } from "react";

type Review = {
  id: number;
  name: string;
  location: string;
  initials: string;
  rating: number;
  date: string;
  title: string;
  body: string;
  verified: boolean;
  helpful: number;
  variant?: string;
};

type QA = {
  id: number;
  question: string;
  asker: string;
  date: string;
  answer: string;
  answerer: string;
  answerDate: string;
};

type Props = {
  productId: number;
  productName: string;
  rating: number;
  reviewCount: number;
};

// Deterministic helper so each product gets a stable mock review set
function seeded(productId: number, salt: number) {
  const x = Math.sin(productId * 9301 + salt * 49297) * 233280;
  return Math.abs(x - Math.floor(x));
}

const REVIEW_POOL: Omit<Review, "id" | "helpful" | "date">[] = [
  {
    name: "Margaret L.",
    location: "Portland, OR",
    initials: "ML",
    rating: 5,
    title: "Even better in person",
    body:
      "I'd been eyeing this for months and finally pulled the trigger. The photos honestly don't do it justice — the weight, the finish, every detail feels considered. It arrived wrapped in linen and a hand-written note from the studio. This is the kind of piece I'll keep forever.",
    verified: true,
    variant: "Natural",
  },
  {
    name: "Daniel R.",
    location: "Brooklyn, NY",
    initials: "DR",
    rating: 5,
    title: "Worth every penny",
    body:
      "I have a hard time buying things at this price point without a lot of hesitation, but this exceeded my expectations. You can feel the craftsmanship the moment you hold it. Three months in and it's already developing a beautiful patina.",
    verified: true,
  },
  {
    name: "Sarah K.",
    location: "Austin, TX",
    initials: "SK",
    rating: 4,
    title: "Beautifully made, just a little smaller than I expected",
    body:
      "The craftsmanship is genuinely impressive and the colors are exactly as shown. Star off only because I wish I'd looked closer at the dimensions before ordering — it's a bit more petite than I had in mind for the space. Still keeping it.",
    verified: true,
    variant: "Cream",
  },
  {
    name: "Eleanor W.",
    location: "Seattle, WA",
    initials: "EW",
    rating: 5,
    title: "A real heirloom piece",
    body:
      "Bought this as a wedding gift and the couple was completely floored. The studio included a little card about the maker — small details like that elevate the whole experience. I'll absolutely be ordering more for the holidays.",
    verified: true,
  },
  {
    name: "James P.",
    location: "Asheville, NC",
    initials: "JP",
    rating: 5,
    title: "Exactly what I was hoping for",
    body:
      "I appreciate that this feels like it was made by hands, not by a machine. Tiny imperfections that make it interesting. Solid, warm, and looks beautiful in our living room next to the woodstove.",
    verified: true,
  },
  {
    name: "Priya M.",
    location: "Chicago, IL",
    initials: "PM",
    rating: 4,
    title: "Lovely, though shipping took a beat",
    body:
      "The piece itself is gorgeous and clearly handmade with care. Took a little longer to arrive than I expected, but the studio kept me updated the whole time. Would order again.",
    verified: true,
    variant: "Walnut",
  },
];

const QA_POOL: Omit<QA, "id">[] = [
  {
    question: "How long does it take to ship?",
    asker: "Anna B.",
    date: "3 weeks ago",
    answer:
      "We hand-pack each order within 2–3 business days and ship via UPS Ground. Most orders arrive within a week, sometimes faster on the West Coast.",
    answerer: "Hearth & Heirloom Studio",
    answerDate: "3 weeks ago",
  },
  {
    question: "Is it safe to use every day, or is it more decorative?",
    asker: "Tom R.",
    date: "1 month ago",
    answer:
      "Made to be used. Everything that leaves our studio is functional first, beautiful second. With basic care it will hold up beautifully for decades.",
    answerer: "Hearth & Heirloom Studio",
    answerDate: "1 month ago",
  },
  {
    question: "Do you offer gift wrapping?",
    asker: "Lila C.",
    date: "2 months ago",
    answer:
      "Yes! Every order ships in unbleached linen with a hand-written card. If you'd like a custom note, just add it at checkout.",
    answerer: "Hearth & Heirloom Studio",
    answerDate: "2 months ago",
  },
];

function ratingBreakdown(productId: number) {
  // Returns a 5-element array of percentages summing to 100, weighted heavily toward 5 stars
  const five = 62 + Math.floor(seeded(productId, 1) * 18); // 62-79
  const four = 14 + Math.floor(seeded(productId, 2) * 10); // 14-23
  const three = 4 + Math.floor(seeded(productId, 3) * 5); // 4-8
  const two = Math.floor(seeded(productId, 4) * 3); // 0-2
  const one = Math.max(0, 100 - five - four - three - two);
  return [five, four, three, two, one];
}

export default function ReviewsSection({
  productId,
  productName,
  rating,
  reviewCount,
}: Props) {
  const tabs = ["Reviews", "Q&A", "Shipping"] as const;
  type Tab = (typeof tabs)[number];
  const [activeTab, setActiveTab] = useState<Tab>("Reviews");
  const [filter, setFilter] = useState<number | "all">("all");
  const [sort, setSort] = useState<"recent" | "helpful">("recent");
  const [helpfulVotes, setHelpfulVotes] = useState<Record<number, boolean>>({});

  const breakdown = useMemo(() => ratingBreakdown(productId), [productId]);

  // Pick 4 reviews from the pool seeded by product id, assign helpful counts + dates
  const reviews: Review[] = useMemo(() => {
    const dateLabels = [
      "2 days ago",
      "1 week ago",
      "3 weeks ago",
      "1 month ago",
      "2 months ago",
    ];
    const startIdx = Math.floor(seeded(productId, 11) * REVIEW_POOL.length);
    const picked: Review[] = [];
    for (let i = 0; i < 4; i += 1) {
      const tpl = REVIEW_POOL[(startIdx + i) % REVIEW_POOL.length];
      picked.push({
        ...tpl,
        id: productId * 10 + i,
        date: dateLabels[i % dateLabels.length],
        helpful: 4 + Math.floor(seeded(productId, 20 + i) * 38),
      });
    }
    return picked;
  }, [productId]);

  const filtered = useMemo(() => {
    let list = reviews;
    if (filter !== "all") {
      list = list.filter((r) => r.rating === filter);
    }
    if (sort === "helpful") {
      list = [...list].sort((a, b) => b.helpful - a.helpful);
    }
    // recent already in order
    return list;
  }, [reviews, filter, sort]);

  const qa: QA[] = QA_POOL.map((q, i) => ({ ...q, id: productId * 100 + i }));

  return (
    <section
      id="reviews"
      className="px-4 md:px-10 lg:px-14 py-16 md:py-24 border-t border-background-200"
    >
      <div className="max-w-6xl mx-auto">
        <header className="mb-8 md:mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-foreground-600 mb-2">
              Voices from the studio
            </p>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 leading-tight">
              What people say about this piece
            </h2>
          </div>
          <div className="text-sm text-foreground-600">
            {reviewCount} reviews · Average {rating.toFixed(1)} of 5
          </div>
        </header>

        {/* Tabs */}
        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 w-fit mb-10">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveTab(t)}
              className={`px-4 sm:px-5 h-9 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === t
                  ? "bg-foreground-950 text-background-50"
                  : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              {t}
              {t === "Reviews" && (
                <span
                  className={`ml-1.5 text-[11px] ${
                    activeTab === t ? "text-background-200" : "text-foreground-500"
                  }`}
                >
                  {reviewCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* REVIEWS TAB */}
        {activeTab === "Reviews" && (
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-10 lg:gap-14 items-start">
            {/* Breakdown panel */}
            <aside className="lg:sticky lg:top-28 rounded-2xl border border-background-200 bg-background-100/40 p-6">
              <div className="flex items-baseline gap-2">
                <span className="font-heading text-5xl text-foreground-950 leading-none">
                  {rating.toFixed(1)}
                </span>
                <span className="text-sm text-foreground-600">/ 5</span>
              </div>
              <div className="mt-2 flex items-center gap-0.5 text-secondary-600">
                {Array.from({ length: 5 }).map((_, i) => {
                  const filled = i < Math.round(rating);
                  return (
                    <i
                      key={i}
                      className={`${filled ? "ri-star-fill" : "ri-star-line"} text-base`}
                    ></i>
                  );
                })}
              </div>
              <p className="mt-1 text-xs text-foreground-600">
                Based on {reviewCount} verified reviews
              </p>

              {/* Breakdown bars */}
              <ul className="mt-6 space-y-2.5">
                {[5, 4, 3, 2, 1].map((star, idx) => {
                  const pct = breakdown[idx];
                  const isActive = filter === star;
                  return (
                    <li key={star}>
                      <button
                        type="button"
                        onClick={() => setFilter(isActive ? "all" : star)}
                        className={`w-full flex items-center gap-3 cursor-pointer text-xs transition-colors ${
                          isActive ? "text-foreground-950" : "text-foreground-700"
                        }`}
                      >
                        <span className="w-8 flex items-center gap-0.5 shrink-0">
                          {star}
                          <i className="ri-star-fill text-[11px] text-secondary-600"></i>
                        </span>
                        <span className="flex-1 h-2 rounded-full bg-background-200/80 overflow-hidden">
                          <span
                            className={`block h-full rounded-full transition-all duration-700 ${
                              isActive ? "bg-foreground-950" : "bg-secondary-500"
                            }`}
                            style={{ width: `${pct}%` }}
                          ></span>
                        </span>
                        <span className="w-9 text-right tabular-nums shrink-0">{pct}%</span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-6 pt-6 border-t border-background-200 space-y-3">
                <p className="text-xs text-foreground-600 leading-relaxed">
                  Reviews are from buyers who actually received {productName}. No paid
                  placements, no incentives.
                </p>
                <button
                  type="button"
                  className="w-full h-10 rounded-full border border-foreground-950 text-foreground-950 text-sm font-medium hover:bg-foreground-950 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Write a review
                </button>
              </div>
            </aside>

            {/* Review list */}
            <div>
              {/* Filter + sort row */}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-4 border-b border-background-200">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFilter("all")}
                    className={`px-3 h-8 rounded-full text-xs whitespace-nowrap cursor-pointer transition-colors ${
                      filter === "all"
                        ? "bg-foreground-950 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200/70"
                    }`}
                  >
                    All
                  </button>
                  {[5, 4, 3, 2, 1].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFilter(filter === star ? "all" : star)}
                      className={`px-3 h-8 rounded-full text-xs whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-1 ${
                        filter === star
                          ? "bg-foreground-950 text-background-50"
                          : "bg-background-100 text-foreground-700 hover:bg-background-200/70"
                      }`}
                    >
                      {star}
                      <i className="ri-star-fill text-[10px]"></i>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-xs text-foreground-600">
                  <span>Sort by</span>
                  <button
                    type="button"
                    onClick={() => setSort("recent")}
                    className={`px-2.5 h-7 rounded-full cursor-pointer transition-colors ${
                      sort === "recent"
                        ? "bg-foreground-950 text-background-50"
                        : "hover:text-foreground-950"
                    }`}
                  >
                    Most recent
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort("helpful")}
                    className={`px-2.5 h-7 rounded-full cursor-pointer transition-colors ${
                      sort === "helpful"
                        ? "bg-foreground-950 text-background-50"
                        : "hover:text-foreground-950"
                    }`}
                  >
                    Most helpful
                  </button>
                </div>
              </div>

              {/* Reviews */}
              {filtered.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-background-300 p-10 text-center">
                  <p className="text-sm text-foreground-700">
                    No reviews match this filter yet.
                  </p>
                  <button
                    type="button"
                    onClick={() => setFilter("all")}
                    className="mt-3 text-sm text-foreground-950 underline underline-offset-4 cursor-pointer"
                  >
                    Show all reviews
                  </button>
                </div>
              ) : (
                <ul className="space-y-6">
                  {filtered.map((r) => {
                    const voted = !!helpfulVotes[r.id];
                    return (
                      <li
                        key={r.id}
                        className="group rounded-2xl border border-background-200 bg-background-50 p-5 md:p-6 hover:border-foreground-300 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <span className="w-11 h-11 shrink-0 rounded-full bg-foreground-950 text-background-50 text-sm font-medium flex items-center justify-center">
                            {r.initials}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                              <span className="text-sm font-medium text-foreground-950">
                                {r.name}
                              </span>
                              {r.verified && (
                                <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-accent-700 bg-accent-100 px-1.5 h-4 rounded-full">
                                  <i className="ri-verified-badge-fill text-[11px]"></i>
                                  Verified
                                </span>
                              )}
                              <span className="text-xs text-foreground-500">·</span>
                              <span className="text-xs text-foreground-600">
                                {r.location}
                              </span>
                            </div>
                            <div className="mt-1.5 flex items-center gap-2">
                              <span className="flex items-center gap-0.5 text-secondary-600">
                                {Array.from({ length: 5 }).map((_, i) => (
                                  <i
                                    key={i}
                                    className={`${
                                      i < r.rating ? "ri-star-fill" : "ri-star-line"
                                    } text-[13px]`}
                                  ></i>
                                ))}
                              </span>
                              <span className="text-xs text-foreground-600">{r.date}</span>
                              {r.variant && (
                                <>
                                  <span className="text-xs text-foreground-400">·</span>
                                  <span className="text-xs text-foreground-600">
                                    {r.variant}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <h3 className="mt-4 text-base font-medium text-foreground-950">
                          {r.title}
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                          {r.body}
                        </p>

                        <div className="mt-4 flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() =>
                              setHelpfulVotes((s) => ({ ...s, [r.id]: !voted }))
                            }
                            className={`inline-flex items-center gap-1.5 text-xs cursor-pointer transition-colors ${
                              voted
                                ? "text-foreground-950"
                                : "text-foreground-600 hover:text-foreground-950"
                            }`}
                          >
                            <i
                              className={`${
                                voted ? "ri-thumb-up-fill" : "ri-thumb-up-line"
                              } text-sm`}
                            ></i>
                            Helpful · {r.helpful + (voted ? 1 : 0)}
                          </button>
                          <button
                            type="button"
                            className="text-xs text-foreground-500 hover:text-foreground-950 cursor-pointer"
                          >
                            Report
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              <div className="mt-8 text-center">
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 text-sm text-foreground-700 hover:text-foreground-950 cursor-pointer group"
                >
                  Load more reviews
                  <i className="ri-arrow-down-line transition-transform group-hover:translate-y-0.5"></i>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Q&A TAB */}
        {activeTab === "Q&A" && (
          <div className="max-w-3xl">
            <ul className="space-y-5">
              {qa.map((q) => (
                <li
                  key={q.id}
                  className="rounded-2xl border border-background-200 bg-background-50 p-5 md:p-6"
                >
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-background-100 text-foreground-950 text-xs font-medium flex items-center justify-center">
                      Q
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground-950">{q.question}</p>
                      <p className="mt-1 text-xs text-foreground-600">
                        Asked by {q.asker} · {q.date}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-start gap-3">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-foreground-950 text-background-50 text-xs font-medium flex items-center justify-center">
                      A
                    </span>
                    <div className="flex-1">
                      <p className="text-sm leading-relaxed text-foreground-700">{q.answer}</p>
                      <p className="mt-1.5 text-xs text-foreground-600">
                        {q.answerer} · {q.answerDate}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-8 rounded-2xl border border-dashed border-background-300 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
              <div>
                <p className="text-sm font-medium text-foreground-950">
                  Got a question we haven't answered?
                </p>
                <p className="text-xs text-foreground-600 mt-1">
                  We usually reply within a day, often the same hour.
                </p>
              </div>
              <button
                type="button"
                className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-foreground-950 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                Ask a question
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        )}

        {/* SHIPPING TAB */}
        {activeTab === "Shipping" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl">
            {[
              {
                icon: "ri-truck-line",
                title: "Carefully packed",
                body: "Wrapped in unbleached linen and recycled cardboard. Every box leaves the studio by hand.",
              },
              {
                icon: "ri-time-line",
                title: "Ships in 2–3 days",
                body: "Most U.S. orders arrive within a week. Free shipping on orders over $150.",
              },
              {
                icon: "ri-arrow-go-back-line",
                title: "30-day returns",
                body: "If it doesn't feel right at home, send it back. We cover return shipping on any defect.",
              },
            ].map((c) => (
              <div
                key={c.title}
                className="rounded-2xl border border-background-200 bg-background-50 p-6"
              >
                <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-background-100 text-foreground-950 mb-4">
                  <i className={`${c.icon} text-lg`}></i>
                </span>
                <h3 className="text-base font-medium text-foreground-950">{c.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{c.body}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}